import * as React from 'react';
import styled from 'styled-components';
import * as P from 'polished';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';

import * as Theme from '../../../serviceTheme';
import { useIORef } from '../../../core/hooks';
import { withConfig } from '../../utils';
import { InputColors, InputConfig } from '../../Input';
import { Layout, Entry, slot, useEffect, useSetter, useGetter, SelfValue } from './slots';

// -------------------------------------------------------------------------------------
// Registry
// -------------------------------------------------------------------------------------
declare module '../../Controls/hkt' {
    interface URIControlsToKind {
        'group-buttons-single': Props
    }
}

// -------------------------------------------------------------------------------------
// Model
// -------------------------------------------------------------------------------------

type Props = {
    name: string,
    data: SelfValue[],
    value: string | null,
    onChange: (x: SelfValue, name: string) => void,
    className?: string,
    disabled?: boolean,
    children?: React.ReactNode,
}

// -------------------------------------------------------------------------------------
// Slots
// -------------------------------------------------------------------------------------

export const Item = slot('item')(({ children, index, className }) => {

    const main = useGetter('main');
    const cursor = useGetter('cursor');
    const [cursorMove] = useEffect('cursorMove');

    const handleClick = () => {
        const data = main.getData();

        main.changeValue(data[index].value);
        cursor.set(index);
    }

    const isActive = React.useMemo(() => {
        return main.getCurrentValue() === main.getData()[index].value;
    }, [cursorMove, index]);

    const x = main.getData()[index];

    return (
        <ItemS 
            onClick={handleClick}
            isActive={isActive}
            className={className}>
            {children(x)}
        </ItemS>
    );
});

const ItemS = styled.button<{ isActive: boolean }>`
    padding-left: .6em;
    padding-right: .6em;
    margin: 0 0.15em;
    color: ${({ isActive }) => isActive ? '#fff' : '#1D2124'};
    background-color: ${({ isActive, theme }) => isActive ? Theme.safeGetPrimary({ theme }) : '#fff'};
    border-radius: 2px;
    box-shadow: ${({ isActive }) => isActive ? '0px 4px 4px rgba(46, 130, 139, 0.19)' : 'none'};

    &:hover {
        ${({ isActive, theme }) => isActive ? ({}) : ({
            borderColor: Theme.safeGetPrimary({ theme }),
            backgroundColor: P.transparentize(0.8, Theme.safeGetPrimary({ theme })),
        })}
    }
`;

export const Inner = slot('inner')(({ className }) => {

    const main = useGetter('main');

    return (
        <InnerS className={className}>
            {main.getData().map((_, index) => {
                return (
                    <Layout 
                        key={index}
                        name="item"
                        index={index}
                    />
                );
            })}
        </InnerS>
    );
});



const InnerS = styled.div`
    display: flex;
    margin: 0 -0.15em;
    height: 100%;
`;


// -------------------------------------------------------------------------------------
// Host
// -------------------------------------------------------------------------------------

export const Group = React.forwardRef<HTMLDivElement, Props>(({ children, ...props }, ref) => {
    return (
        <Entry connectLayout={children}>
            <Cursor />
            <Core innerRef={ref} {...props} />
        </Entry>
    );
});


// -------------------------------------------------------------------------------------
// Core
// -------------------------------------------------------------------------------------


const Core: React.FunctionComponent<Props & { innerRef: ((instance: HTMLDivElement | null) => void) | React.MutableRefObject<HTMLDivElement | null> | null }> = ({
    name, 
    data, 
    value, 
    onChange, 
    className, 
    disabled = false,
}) => {

    const preparedValue = React.useMemo(() => pipe(
        value,
        O.fromNullable,
        O.fold(
            () => null,
            (r) => r,
        ),
    ), [value]);

    const { read: getCurrentValue, write: _changeValue } = useIORef(preparedValue);
    const { read: getData, write: changeData } = useIORef(data);

    React.useEffect(() => {
        _changeValue(preparedValue)();
    }, [preparedValue]);

    React.useEffect(() => {
        changeData(data);
    }, [data]);

    const [, runChangeValue] = useEffect('changeValue');

    const changeValue = runChangeValue((x) => {
        _changeValue(x)();
        onChange(x, name);
    });


    useSetter('main', {
        name,
        getData,
        getCurrentValue,
        changeValue,
    });

    return (
        <Container 
            className={className}
            disabled={disabled}
            tabIndex={0}>
            <Layout name="inner" />
        </Container>
    );
}




const Container_ = styled.div`
    padding: .3em;

    ${InputColors.rest}
    ${InputColors.normal}

    width: max-content;

    &[value=""] {
        ${InputColors.emptyValue}
    }

    &:disabled {
        ${InputColors.disabled}

        &[value=""] {
            ${InputColors.disabledEmptyValue}
        }
    }

    &:focus {
        ${InputColors.focus}
    }

    &:not(:focus):not(:disabled):hover {
        ${InputColors.hover}

        &[value=""] {
            ${InputColors.hoverEmptyValue}
        }
    }

    &::placeholder {
        color: #898f9e;
    }
`;


export const Container = withConfig<any, HTMLDivElement>()(InputConfig)(Container_);


const Cursor: React.FunctionComponent = () => {

    const { 
        read: get, 
        write: _setCursor,
        modify: _modify
    } = useIORef(0);

    const [, runCursorMove] = useEffect('cursorMove');

    const set = runCursorMove((n: number) => {
        _setCursor(n)();
    });

    const modify = runCursorMove((f: (x: number) => number) => {
        _modify(f)();
    })


    useSetter('cursor', {
        get,
        set,
        modify,
    });

    return null;
}
